import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Divider,
  Input,
  Sheet,
  Typography,
} from "@mui/joy";
import { Box, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Project_Entity } from "../../entities/project";
import {
  formOpenSelector,
  selectedProjectIDsSelector,
  setFormOpen,
  setSelectedProjectIDs,
  setTmpGig,
} from "../../reducers/rhapsody";
import {
  useCreateProjectMutation,
  useDeleteProjectMutation,
  useUpdateProjectMutation,
} from "../../redux/project/projectEndpoints";
import { useProject } from "../../redux/project/projectHooks";
import { useAskQuestion } from "../askQuestion/askQuestion";
import DialogClose from "../dialogClose/dialogClose";
import { GooglePlaceSuggestions } from "../googlePlaceSuggestions/googlePlaceSuggestions";
import { usePro } from "../usePro/usePro";
import { NerdPhoneField } from "@nerdjs/nerd-ui";
import Upload from "../fileUpload/fileUpload";
import ColorPicker from "../colorPicker/colorPicker";
import { useSeasons } from "../../redux/season/seasonHooks";
import { Season } from "../../entities/season";
import {
  DialogHeader,
  FieldLabel,
  GroupCaption,
  dialogPaperSx,
  groupSheetSx,
  pillButtonSx,
  tileColors,
} from "../formKit/formKit";

/**
 *
 * @returns {ReactElement} FormProject page
 */
export function FormProject() {
  const projectIDs = useSelector(selectedProjectIDsSelector);
  const { project } = useProject(projectIDs);
  const open = useSelector(formOpenSelector("project"));
  const gigFormOpen = useSelector(formOpenSelector("gig"));
  const [_project, setProject] = useState<Partial<Project_Entity>>({});
  const pro = usePro();
  const [createProject] = useCreateProjectMutation();
  const [updateProject] = useUpdateProjectMutation();
  const [deleteProject] = useDeleteProjectMutation();

  const { seasons } = useSeasons();
  const local420Seasons = seasons.filter((p) => p.sourceID === 420);

  const askQuestion = useAskQuestion();

  const dispatch = useDispatch();

  useEffect(() => {
    if (project?.id) {
      setProject(project);
    }
  }, [project]);

  const onClose = () => {
    setProject({});
    dispatch(setSelectedProjectIDs());
    dispatch(setFormOpen({ isOpen: false, formID: "project" }));
  };

  const save = async () => {
    if (!pro)
      return dispatch(setFormOpen({ formID: "upgradeOpen", isOpen: true }));
    if (projectIDs && _project) {
      updateProject({ id: projectIDs.id, body: _project });
    } else if (_project) {
      const promise = createProject(_project);
      // Quick-add from the Service form: select the new project straight into
      // the open gig draft (the list query refreshes it into the dropdown).
      if (gigFormOpen) {
        try {
          const created = await promise.unwrap();
          if (created?.id) dispatch(setTmpGig({ projectID: created.id }));
        } catch (e) {
          console.error(e);
        }
      }
    }
    onClose();
  };

  const askDelete = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: <Typography>You are about to delete a Project.</Typography>,
    }).then((i) => {
      if (i == 1) confirmDelete();
    });
  };

  const confirmDelete = async () => {
    if (projectIDs?.id) deleteProject(projectIDs?.id);
    onClose();
  };

  return (
    <DialogClose
      open={open}
      onClose={onClose}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle>
        <DialogHeader
          title={`${projectIDs?.id ? "Edit" : "Create"} Project`}
          subtitle={
            !projectIDs?.id
              ? "A project gathers everything for one program in one place."
              : undefined
          }
        />
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: 500,
          maxWidth: "100%",
        }}
      >
        {!pro ? (
          <Alert
            color="info"
            startDecorator={<i className="fa-regular fa-circle-info"></i>}
          >
            Add Projects you play outside of Rhapsody allowing you to manage all
            your work seamlessly in one platform.
          </Alert>
        ) : (
          []
        )}
        <GroupCaption optional>Cover artwork</GroupCaption>
        <Sheet variant="soft" sx={{ ...groupSheetSx, p: 1 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            {_project.coverURL ? (
              <img
                src={_project.coverURL}
                style={{ width: 95, height: 95, borderRadius: "8px" }}
              />
            ) : (
              <Upload
                onUpload={(f) => setProject((t) => ({ ...t, coverURL: f }))}
                cropDimension={{ width: 250, height: 250 }}
              >
                <Box
                  sx={{
                    width: 95,
                    height: 95,
                    flexShrink: 0,
                    border: "dashed 1.5px #9e9e9e",
                    borderRadius: "8px",
                    alignItems: "center",
                    display: "flex",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography level="h4" sx={{ color: "#9e9e9e" }}>
                    <i className="fa-duotone fa-image"></i>
                  </Typography>
                </Box>
              </Upload>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                level="body2"
                sx={{ color: "black", fontWeight: 600 }}
              >
                Project Cover
              </Typography>
              <Typography level="body3">
                An artwork respresenting the Project. It must be at least 250px
                by 250px with a max size of 512KB. The supported MIME types are
                JPG, PNG, and GIf.
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Upload
                  cropDimension={{ width: 250, height: 250 }}
                  onUpload={(f) => setProject((t) => ({ ...t, coverURL: f }))}
                />
                {_project.coverURL ? (
                  <Chip
                    onClick={() => setProject((t) => ({ ...t, coverURL: "" }))}
                    size="sm"
                    variant="outlined"
                    color="danger"
                  >
                    Remove Cover
                  </Chip>
                ) : (
                  []
                )}
              </Box>
            </Box>
          </Box>
        </Sheet>
        <GroupCaption>The basics</GroupCaption>
        <Sheet variant="soft" sx={groupSheetSx}>
          <Input
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            autoFocus
            placeholder="e.g. Beethoven Festival"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-signature"
                color={tileColors.blue}
                label="Name"
              />
            }
            value={_project.name}
            onChange={(e) =>
              setProject((v) => ({ ...v, name: e.target.value }))
            }
            type="text"
          />
          <Divider />
          <Input
            sx={{ background: "transparent" }}
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            variant="plain"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-align-left"
                color={tileColors.indigo}
                label="Description"
              />
            }
            value={_project.description}
            onChange={(e) =>
              setProject((v) => ({ ...v, description: e.target.value }))
            }
            type="text"
          />
          <Divider />
          <Autocomplete
            sx={{ background: "transparent" }}
            variant="plain"
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-ticket"
                color={tileColors.purple}
                label="Season"
              />
            }
            options={local420Seasons}
            getOptionLabel={(option) => (option as unknown as Season).name}
            slotProps={{
              input: { style: { textAlign: "right" } },
              listbox: {
                sx: { zIndex: 9999 },
              },
            }}
            onChange={(e, p) =>
              setProject((v) => ({
                ...v,
                seasonID: (p as unknown as Season).id,
              }))
            }
          />
        </Sheet>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {projectIDs?.id ? (
          <Button
            variant="soft"
            onClick={askDelete}
            color="danger"
            sx={pillButtonSx}
          >
            Delete
          </Button>
        ) : (
          []
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={onClose}
          color="neutral"
          variant="plain"
          sx={pillButtonSx}
        >
          Cancel
        </Button>
        <Button
          sx={{ ...pillButtonSx, px: 3 }}
          endDecorator={
            !pro ? (
              <Typography
                sx={{ color: "white" }}
                variant="outlined"
                color="neutral"
                level="body5"
              >
                PRO
              </Typography>
            ) : undefined
          }
          onClick={save}
          color="info"
        >
          {projectIDs?.id ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </DialogClose>
  );
}
